import React, { Component } from 'react';
import moment, { ISO_8601, now } from 'moment-timezone';
import { every, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { updateSession } from 'actions/inPersonTraining/eventOverview/sessions';
import { Button, Card } from 'ui';
import { VIDEO_CONFERENCE_TYPES } from 'constants/config';
import { isValidURL } from 'utils/string_utils';
import SessionForm from 'components/InPersonTraining/shared/SessionForm';
import TimeZone from 'components/InPersonTraining/shared/SessionForm/TimeZone';
import Error, {
  getErrorMessage,
} from 'components/InPersonTraining/shared/Error';
import serializeSessionToFormState from './serializeSessionToFormState';

const isPast = date => moment(date, ISO_8601).isBefore(now());

class EditForm extends Component {
  state = {
    loading: false,
    error: null,
    session: serializeSessionToFormState(this.props.session),
  };

  get isValid() {
    return every([
      moment(this.state.session.startTime).isValid(),
      moment(this.state.session.endTime).isValid(),
      !isEmpty(this.state.session.timeZone),
      this.isVideoConferencingValid(),
    ]);
  }

  isVideoConferencingValid() {
    const { type, url } = this.state.session.videoMeeting;

    if (type !== 'other') return true;

    return isValidURL(url);
  }

  handleFormStateChange = session => {
    this.setState({ session });
  };

  handleSessionInputChange = e => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      session: { ...prevState.session, [name]: value },
    }));
  };

  handleSessionNameChange = (e, sessionId) => {
    const name = e.target.value;
    this.setState(prevState => ({
      session: { ...prevState.session, name },
    }));
  };

  handleEndTimeChange = endTime => {
    this.setState(prevState => ({
      session: { ...prevState.session, endTime },
    }));
  };

  handleStartTimeChange = date => {
    this.setState(prevState => ({
      session: {
        ...prevState.session,
        startTime: date,
        endTime: moment(date).set({
          hour: moment(prevState.session.endTime).get('hour'),
          minute: moment(prevState.session.endTime).get('minute'),
        }),
      },
    }));
  };

  handleTimeZoneChange = timeZone => {
    this.setState(
      prevState => ({
        session: { ...prevState.session, timeZone },
      }),
      () => {
        TimeZone.setLastSelected(timeZone);
      },
    );
  };

  handleTrainerChange = trainerId => {
    this.setState(prevState => ({
      session: { ...prevState.session, trainerId },
    }));
  };

  resetMaxAttendees(capOption) {
    if (capOption) return;
    this.setState(prevState => ({
      session: { ...prevState.session, maxAttendees: '' },
    }));
  }

  handleCapOptionsChange = e => {
    const capOption = JSON.parse(e.target.value);
    this.setState(
      prevState => ({
        session: { ...prevState.session, capOption },
      }),
      () => {
        this.resetMaxAttendees(capOption);
      },
    );
  };

  handleVideoConferenceChange = data => {
    this.setState(prevState => ({
      session: { ...prevState.session, videoMeeting: data },
    }));
  };

  handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ loading: true, error: null });

    const sessionData = {
      ...this.state.session,
      id: this.props.session.id,
      startTime: moment(this.state.session.startTime).tz('utc', true).format(),
      endTime: moment(this.state.session.endTime).tz('utc', true).format(),
    };

    this.props
      .updateSession(sessionData)
      .then(() => {
        this.setState({ loading: false }, this.props.onDismiss);
      })
      .catch(error => {
        this.setState({
          loading: false,
          error: error?.status === 428 ? null : error,
        });
      });
  };

  render() {
    const { currentCompany, currentUser, isMultiSession, timeZones, users } =
      this.props;
    const { error, session } = this.state;
    const isNewZoomMeeting =
      session.videoMeeting.type === VIDEO_CONFERENCE_TYPES.ZOOM &&
      session.videoMeeting.url === null;
    const errorMessage = error && getErrorMessage(error);
    const isVideoMeetingHost =
      session?.videoMeeting?.host_id === currentUser.id ||
      isNewZoomMeeting ||
      session?.videoMeeting.type !== VIDEO_CONFERENCE_TYPES.ZOOM;

    return (
      <Card.Content padding="large">
        {errorMessage && (
          <div className="ErrorContainer ErrorContainer--brandRed mb-16 Animated Animated--fadeIn">
            {errorMessage}
          </div>
        )}
        <form onSubmit={this.handleSubmit}>
          <SessionForm
            isEditingSession
            canEditTime={!isPast(this.props.session.start_time_utc)}
            currentCompany={currentCompany}
            currentUser={currentUser}
            handleCapOptionsChange={this.handleCapOptionsChange}
            handleEndTimeChange={this.handleEndTimeChange}
            handleSessionInputChange={this.handleSessionInputChange}
            handleSessionNameChange={this.handleSessionNameChange}
            handleStartTimeChange={this.handleStartTimeChange}
            handleTimeZoneChange={this.handleTimeZoneChange}
            handleTrainerChange={this.handleTrainerChange}
            handleVideoConferenceChange={this.handleVideoConferenceChange}
            isMultiSession={isMultiSession}
            isVideoMeetingHost={isVideoMeetingHost}
            session={this.state.session}
            timeZones={timeZones}
            users={users}
          />
          {error !== null && !error?.message && <Error {...error} />}
          <div className="d-f jc-sb ai-c mt-24">
            <button
              type="button"
              disabled={this.props.loading}
              onClick={this.props.onDismiss}
              className="u-linkBtn"
            >
              {t('common.cancel')}
            </button>
            <Button
              customColor
              tall
              submit
              loading={this.state.loading}
              disabled={!this.isValid}
            >
              {isNewZoomMeeting
                ? t(
                    'in_person_training.event_overview.sessions.update_with_zoom',
                  )
                : t('in_person_training.event_overview.sessions.update')}
            </Button>
          </div>
        </form>
      </Card.Content>
    );
  }
}

const mapStateToProps = state => ({
  timeZones: state.meta.timeZones,
  users: state.meta.users,
});

const mapDispatchToProps = {
  updateSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditForm);
